import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, EmptyState, Eyebrow, Spinner } from '@busbook/ui';
import { useTenant } from '../../lib/tenant.js';
import { useCustomer } from '../../lib/customer-session.js';
import { api } from '../../lib/api.js';
import { groupTrips } from '../../lib/trip-groups.js';
import { useT } from '../../i18n/index.js';
import { TripCard } from './TripCard.js';
import { PastTrips } from './PastTrips.js';
import { ClaimForm } from './ClaimForm.js';
import { InstallPrompt } from '../../components/InstallPrompt.js';
import type { BookingView } from '../Ticket.js';

/**
 * My trips (docs/05 "Booking management").
 *
 * Two groups, and only two: what is still to come, open; and what has already happened,
 * collapsed underneath.
 *
 * Every booking now belongs to an account (DECISIONS D68), so a signed-out visitor is not
 * shown an empty list — an empty list is a statement that they have no trips, and we do not
 * know that. They are asked to sign in instead.
 */
export function MyTrips() {
  const t = useT();
  const { settings, branding } = useTenant();
  const { user, loading: sessionLoading } = useCustomer();
  const [bookings, setBookings] = useState<BookingView[] | null>(null);

  const load = useCallback(async () => {
    try {
      setBookings(await api.get<BookingView[]>('/public/bookings'));
    } catch (err) {
      // A 401 here is the session having lapsed mid-view; the provider clears the user and
      // this screen falls through to the sign-in invitation below.
      if (!(err instanceof ApiError)) throw err;
      setBookings([]);
    }
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    if (!user) {
      setBookings([]);
      return;
    }
    void load();
  }, [sessionLoading, user, load]);

  const { upcoming, past } = useMemo(
    () => groupTrips(bookings ?? [], Date.now()),
    [bookings],
  );

  return (
    <section className="px-4 py-6">
      <Eyebrow>{t('c.myTrips')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{t('m.yourBookings')}</h1>

      {sessionLoading || (user && bookings === null) ? (
        <div className="grid place-items-center py-16">
          <Spinner label={t('m.loading')} />
        </div>
      ) : !user ? (
        <EmptyState
          title={t('m.signInToSee')}
          description={t('m.signInToSeeHint')}
          action={
            <Link
              to="/auth/login?next=%2Fbookings"
              className="text-sm font-medium underline underline-offset-4"
              style={{ color: 'var(--color-primary)' }}
            >
              {t('c.signIn')}
            </Link>
          }
        />
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="mt-6">
              {/* The soonest departure is why anybody opens this screen, so it is named
                  rather than left as the first of a list. groupTrips has already sorted
                  soonest-first, so index 0 IS the next one. */}
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-sm font-semibold">{t('m.nextDeparture')}</h2>
                {upcoming.length > 1 && (
                  <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    {t('m.moreUpcoming', { count: upcoming.length - 1 })}
                  </span>
                )}
              </div>

              <ul className="mt-2 flex flex-col gap-2">
                <TripCard
                  key={upcoming[0]!.reference}
                  booking={upcoming[0]!}
                  timezone={settings.timezone}
                />
              </ul>

              {upcoming.length > 1 && (
                <>
                  <h2 className="mt-7 text-sm font-semibold">{t('m.upcoming')}</h2>
                  <ul className="mt-2 flex flex-col gap-2">
                    {upcoming.slice(1).map((booking) => (
                      <TripCard
                        key={booking.reference}
                        booking={booking}
                        timezone={settings.timezone}
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <PastTrips bookings={past} timezone={settings.timezone} />

          {upcoming.length === 0 && past.length === 0 && (
            <EmptyState
              title={t('m.none')}
              description={t('m.noneHint')}
              action={
                <Link
                  to="/"
                  className="text-sm font-medium underline underline-offset-4"
                  style={{ color: 'var(--color-primary)' }}
                >
                  {t('m.findDeparture')}
                </Link>
              }
            />
          )}

          <div className="mt-8">
            <InstallPrompt appName={branding.appName} />
          </div>

          {/* Only for someone signed in: this adds a trip to an account, so without one
              there is nothing for it to add to. */}
          <div className="mt-8">
            <ClaimForm onClaimed={() => void load()} />
          </div>
        </>
      )}
    </section>
  );
}
